<template>
  <!-- In space context, skip scrollbar wrapper — FC's space layout handles scrolling -->
  <div v-if="isSpaceContext" class="fca-events-content">
    <component :is="currentComponent" v-bind="currentProps" v-on="currentEvents" />
  </div>
  <!-- Normal (global) context — provide our own scrollbar -->
  <div v-else class="el-scrollbar">
    <div class="el-scrollbar__wrap" style="height: calc(100vh - var(--fcom-header-height, 65px)); overflow-y: auto;">
      <div class="el-scrollbar__view fca-events-content">
        <component :is="currentComponent" v-bind="currentProps" v-on="currentEvents" />
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import EventsView     from './components/EventsView.vue'
import EventDetailView from './components/EventDetailView.vue'
import CreateEventView from './components/CreateEventView.vue'

const portalBase = ((window.FcaEventsConfig || {}).portalBase || '').replace(/^https?:\/\/[^/]+/, '').replace(/\/+$/, '')
const isSpaceContext = !!(window.FcaSpaceContext?.isSpaceContext)

// Detect space context — set by fca-events-spaces when mounted inside a space
const spaceCtx = window.FcaSpaceContext
const spacePrefix = (spaceCtx?.isSpaceContext && spaceCtx.spaceSlug)
  ? `/space/${spaceCtx.spaceSlug}`
  : ''

const view           = ref('list')
const currentSlug    = ref(null)
const currentEventId = ref(null)

const currentComponent = computed(() => {
  if (view.value === 'detail') return EventDetailView
  if (view.value === 'create') return CreateEventView
  if (view.value === 'edit') return CreateEventView
  return EventsView
})

const currentProps = computed(() => {
  if (view.value === 'detail') return { slug: currentSlug.value }
  if (view.value === 'edit') return { eventId: currentEventId.value, eventSlug: currentSlug.value }
  return {}
})

const currentEvents = computed(() => {
  if (view.value === 'detail') return { 'go-back': navigateToList, 'edit-event': navigateToEdit, deleted: navigateToList }
  if (view.value === 'create') return { saved: onSaved, cancel: navigateToList }
  if (view.value === 'edit') return { saved: onSaved, cancel: onEditCancel }
  return { 'navigate-to-event': navigateToEvent, 'create-event': navigateToCreate, 'go-settings': navigateToSettings, 'edit-event': navigateToEdit }
})

function parsePath(rawPath) {
  // Strip portalBase prefix (e.g. /portal) then space prefix (e.g. /space/start-here)
  const base = portalBase || '/portal'
  const escapedBase = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  let path = rawPath.replace(new RegExp('^' + escapedBase), '')
  if (spacePrefix) {
    const escapedSpace = spacePrefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    path = path.replace(new RegExp('^' + escapedSpace), '')
  }

  if (/^\/events\/create\/?$/.test(path)) {
    view.value = 'create'
    currentSlug.value = null
    currentEventId.value = null
    return
  }

  const editMatch = path.match(/^\/events\/edit\/(\d+)/)
  if (editMatch?.[1]) {
    view.value = 'edit'
    currentEventId.value = parseInt(editMatch[1])
    currentSlug.value = null
    return
  }

  // Match /event/slug (global) or /events/slug (space context detail)
  const detailMatch = path.match(/^\/event\/([^/?#]+)/)
    || (spacePrefix && path.match(/^\/events\/([^/?#]+)/))
  if (detailMatch?.[1]) {
    view.value = 'detail'
    currentSlug.value = detailMatch[1]
    currentEventId.value = null
    return
  }

  view.value = 'list'
  currentSlug.value = null
  currentEventId.value = null
}

function navigate(path) {
  window.history.pushState({}, '', portalBase + spacePrefix + path)
  parsePath(path)
}

function navigateToList()            { navigate('/events') }
function navigateToCreate()          { navigate('/events/create') }
function navigateToSettings()        { window.location.href = portalBase + '/admin/settings/events' }
function navigateToEvent(slug)       {
  // In space context, detail lives under /events/slug (child route of space parent)
  // In global context, detail lives at /event/slug
  navigate(spacePrefix ? `/events/${slug}` : `/event/${slug}`)
}
function navigateToEdit(slug, id)    { currentEventId.value = id; navigate(`/events/edit/${id}`) }

function onSaved(event) {
  event?.slug ? navigateToEvent(event.slug) : navigateToList()
}

function onEditCancel() {
  view.value === 'edit' && currentSlug.value
    ? navigateToEvent(currentSlug.value)
    : navigateToList()
}

// Respond to FC route events
function onFcaRoute(e) { parsePath(e.detail.path) }
function onPopState()  { parsePath(window.location.pathname) }

onMounted(() => {
  parsePath(window.location.pathname)
  window.addEventListener('popstate', onPopState)
  window.addEventListener('fca-events-route', onFcaRoute)

  if (window.FluentCommunityUtil?.hooks) {
    window.FluentCommunityUtil.hooks.addAction(
      'fluent_com_route_changed',
      'fca_events_route_watcher',
      () => setTimeout(() => parsePath(window.location.pathname), 50),
    )
  }

  // In space context, listen for clicks on the space "Events" tab to return to list
  if (spacePrefix) {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href]')
      if (!a) return
      const href = a.getAttribute('href') || ''
      // If clicking a link that ends with the space events base path, go to list view
      if (href.endsWith(spacePrefix + '/events') || href.endsWith(spacePrefix + '/events/')) {
        if (view.value !== 'list') {
          e.preventDefault()
          e.stopPropagation()
          navigateToList()
        }
      }
    }, true)
  }
})

onUnmounted(() => {
  window.removeEventListener('popstate', onPopState)
  window.removeEventListener('fca-events-route', onFcaRoute)
  // FC hooks: addAction exists in all versions; removeAction is not always exposed (or uses a different API).
  const hooks = window.FluentCommunityUtil?.hooks
  if (hooks && typeof hooks.removeAction === 'function') {
    hooks.removeAction('fluent_com_route_changed', 'fca_events_route_watcher')
  }
})
</script>
